import React, {lazy} from 'react';

import sessionStore from '../Session/SessionStore';
import AuthorDashboardProvider from '../AuthorDashboard/AuthorDashboardProvider/AuthorDashboardProvider.react';
import {makeSubjectsRoute} from 'client/Dennis/V2/Subjects/routes';
import {makeBatchRoute} from 'client/Dennis/BatchUtils/routes';
import {
  createRoute,
  redirectRoute,
  layoutComponent,
  withRouter,
  guardRedirect
} from 'client/routeHelpers';

const GuideLevelPracticeView = lazy(
  () => import('./GuideLevelPracticeView/GuideLevelPracticeView')
);

const Dennis = lazy(() => import(/* webpackChunkName: "Dennis" */ './Dennis'));
/**
 * "Content" Components
 */
const Content = lazy(() => import('./Content/Content'));
const Queue = lazy(() => import('./Content/Queue/Queue'));
const SupplementManager = lazy(
  () => import('client/Supplements/SupplementManager/SupplementManager')
);
const Standards = lazy(() => import('./StandardsV2/Standards.react'));
const TagCategories = lazy(() => import('./TagCategories/TagCategories.react'));
/**
 * "Users" Components
 */
const Users2 = lazy(() => import('./Users2/Users'));
const UserSearchWrapper = lazy(() => import('./UserEdit2/UserEditSearchWrapper'));
const UserForm = lazy(() => import('./UserEdit2/UserForm/UserForm'));
const UserWrapper = lazy(() => import('./UserEdit2/Wrapper/UserFormWrapper'));
const UserSubscriptions = lazy(() => import('./UserEdit2/Subscriptions/UserSubscriptions'));
const UserAuthoringPermissions = lazy(
  () => import('./UserEdit2/Authoring/UserAuthoringPermissions')
);
const UserAccess = lazy(() => import('./UserAccess/UserAccess'));
/**
 * "Schools" Components
 */
const SchoolsLanding = lazy(() => import('./Schools/SchoolsLanding/SchoolsLanding.react'));
const SchoolsIndex = lazy(() => import('./Schools/SchoolsIndex/SchoolsIndex.react'));
const SchoolEdit = lazy(() => import('./Schools/SchoolsIndex/Edit/SchoolEdit'));
const SchoolCreate = lazy(() => import('./Schools/SchoolsIndex/Create/SchoolCreate'));
const SubjectGroupsIndex = lazy(() => import('./Schools/SubjectGroups/SubjectGroupsIndex.react'));
const SubjectGroupEdit = lazy(() => import('./Schools/SubjectGroups/Edit/SubjectGroupEdit.react'));
const SubjectGroupCreate = lazy(
  () => import('./Schools/SubjectGroups/Create/SubjectGroupCreate.react')
);

const DistrictsLanding = lazy(() => import('./Districts/DistrictsLanding'));
const DistrictsList = lazy(() => import('./Districts/DistrictsList'));
const DistrictEdit = lazy(() => import('./Districts/Edit/DistrictEdit'));
const DistrictCreate = lazy(() => import('./Districts/Create/DistrictCreate'));

const AuthorDashboard = lazy(() => import('../AuthorDashboard/AuthorDashboard.react'));

const AuthorDashboardWithContext = (props) => {
  return (
    <AuthorDashboardProvider>
      <AuthorDashboard {...props} />
    </AuthorDashboardProvider>
  );
};

const requireContentManager = guardRedirect(
  () => sessionStore.hasContentManagerAccess(),
  '/dennis/content/queue'
);

export function makeDennisRoutes(parentRoute) {
  const dennisRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: 'dennis',
    staticData: {name: 'dennis'},
    component: layoutComponent(Dennis)
  });

  // --- content (layout) ---
  const contentRoute = createRoute({
    getParentRoute: () => dennisRoute,
    path: 'content',
    component: layoutComponent(Content)
  });
  contentRoute.addChildren([
    redirectRoute({
      getParentRoute: () => contentRoute,
      path: 'question-set-search',
      to: '/dennis/content/queue'
    }),
    redirectRoute({
      getParentRoute: () => contentRoute,
      path: 'frq-queue',
      to: '/dennis/content/queue'
    }),
    redirectRoute({getParentRoute: () => contentRoute, path: '/', to: '/dennis/content/queue'}),
    createRoute({
      getParentRoute: () => contentRoute,
      path: 'queue/{-$questionSetId}/{-$questionId}',
      staticData: {title: 'Queue | Albert.io', dennisOnly: true},
      component: withRouter(Queue)
    }),
    createRoute({
      getParentRoute: () => contentRoute,
      path: 'dashboard',
      staticData: {title: 'Authoring | Albert.io'},
      component: withRouter(AuthorDashboardWithContext)
    }),
    makeSubjectsRoute(contentRoute),
    createRoute({
      getParentRoute: () => contentRoute,
      path: 'supplements',
      staticData: {title: 'Supplements | Albert.io', dennisOnly: true},
      beforeLoad: requireContentManager,
      component: withRouter(SupplementManager)
    }),
    (() => {
      const standardsRoute = createRoute({
        getParentRoute: () => contentRoute,
        path: 'standards',
        staticData: {title: 'Standards | Dennis | Albert.io'},
        beforeLoad: requireContentManager,
        component: layoutComponent(Standards)
      });
      return standardsRoute.addChildren([
        createRoute({
          getParentRoute: () => standardsRoute,
          path: '$subjectId',
          component: withRouter(Standards)
        })
      ]);
    })(),
    createRoute({
      getParentRoute: () => contentRoute,
      path: 'tag-categories',
      staticData: {title: 'Tag Categories | Dennis | Albert.io'},
      beforeLoad: requireContentManager,
      component: withRouter(TagCategories)
    })
  ]);

  // --- users (layout) ---
  const usersRoute = createRoute({
    getParentRoute: () => dennisRoute,
    path: 'users',
    component: layoutComponent(Users2)
  });

  const userEditRoute = createRoute({
    getParentRoute: () => usersRoute,
    path: 'edit/$userId',
    staticData: {title: 'User Edit | Dennis | Albert.io'},
    component: layoutComponent(UserWrapper)
  });
  userEditRoute.addChildren([
    createRoute({
      getParentRoute: () => userEditRoute,
      path: '/',
      component: withRouter(UserForm)
    }),
    createRoute({
      getParentRoute: () => userEditRoute,
      path: 'subscriptions',
      staticData: {title: 'User Subscriptions Edit | Dennis | Albert.io'},
      component: withRouter(UserSubscriptions)
    }),
    createRoute({
      getParentRoute: () => userEditRoute,
      path: 'authoring-permissions',
      staticData: {title: 'User Authoring Permissions Edit | Dennis | Albert.io'},
      component: withRouter(UserAuthoringPermissions)
    })
  ]);

  const userCreateRoute = createRoute({
    getParentRoute: () => usersRoute,
    path: 'create',
    staticData: {title: 'User Create | Dennis | Albert.io'},
    component: layoutComponent(UserWrapper)
  });
  userCreateRoute.addChildren([
    createRoute({
      getParentRoute: () => userCreateRoute,
      path: '/',
      component: withRouter(UserForm)
    })
  ]);

  usersRoute.addChildren([
    redirectRoute({getParentRoute: () => usersRoute, path: '/', to: '/dennis/users/search'}),
    createRoute({
      getParentRoute: () => usersRoute,
      path: 'search',
      component: withRouter(UserSearchWrapper)
    }),
    userEditRoute,
    userCreateRoute,
    createRoute({
      getParentRoute: () => usersRoute,
      path: 'access',
      staticData: {title: 'User Access | Dennis | Albert.io'},
      component: withRouter(UserAccess)
    })
  ]);

  // --- districts (layout) ---
  const districtsRoute = createRoute({
    getParentRoute: () => dennisRoute,
    path: 'districts',
    component: layoutComponent(DistrictsLanding)
  });
  districtsRoute.addChildren([
    redirectRoute({
      getParentRoute: () => districtsRoute,
      path: '/',
      to: '/dennis/districts/districts-list'
    }),
    createRoute({
      getParentRoute: () => districtsRoute,
      path: 'districts-list',
      component: withRouter(DistrictsList)
    }),
    createRoute({
      getParentRoute: () => districtsRoute,
      path: 'create',
      component: withRouter(DistrictCreate)
    }),
    createRoute({
      getParentRoute: () => districtsRoute,
      path: 'edit/$districtId',
      component: withRouter(DistrictEdit)
    })
  ]);

  // --- schools (layout) ---
  const schoolsRoute = createRoute({
    getParentRoute: () => dennisRoute,
    path: 'schools',
    component: layoutComponent(SchoolsLanding)
  });
  schoolsRoute.addChildren([
    redirectRoute({
      getParentRoute: () => schoolsRoute,
      path: '/',
      to: '/dennis/schools/school-list'
    }),
    createRoute({
      getParentRoute: () => schoolsRoute,
      path: 'school-list',
      component: withRouter(SchoolsIndex)
    }),
    createRoute({
      getParentRoute: () => schoolsRoute,
      path: 'school-list/create',
      component: withRouter(SchoolCreate)
    }),
    createRoute({
      getParentRoute: () => schoolsRoute,
      path: 'school-list/edit/$schoolId',
      component: withRouter(SchoolEdit)
    }),
    createRoute({
      getParentRoute: () => schoolsRoute,
      path: 'subject-groups',
      component: withRouter(SubjectGroupsIndex)
    }),
    createRoute({
      getParentRoute: () => schoolsRoute,
      path: 'subject-groups/create',
      component: withRouter(SubjectGroupCreate)
    }),
    createRoute({
      getParentRoute: () => schoolsRoute,
      path: 'subject-groups/edit/$subjectGroupId',
      component: withRouter(SubjectGroupEdit)
    })
  ]);

  return dennisRoute.addChildren([
    makeBatchRoute(dennisRoute),
    contentRoute,
    usersRoute,
    districtsRoute,
    schoolsRoute,
    createRoute({
      getParentRoute: () => dennisRoute,
      path: '$subjectSlug/$guideLevelSlug',
      staticData: {dennisOnly: true},
      component: withRouter(GuideLevelPracticeView)
    })
  ]);
}
